import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';
import { useSuperAdminAuth } from '../contexts/SuperAdminAuthContext';
import '../styles/superadmin/sa-base.css'; // loads the Fraunces / Familjen Grotesk fonts

function SuperAdminLogin() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { currentSuperAdmin, loading: authLoading } = useSuperAdminAuth();

  const { email, password } = formData;

  // Already signed in as a SuperAdmin — skip the form
  useEffect(() => {
    if (!authLoading && currentSuperAdmin) {
      navigate('/superadmin/dashboard', { replace: true });
    }
  }, [currentSuperAdmin, authLoading, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const superAdminDoc = await getDoc(doc(db, 'superAdmins', userCredential.user.uid));
      if (!superAdminDoc.exists()) {
        await auth.signOut();
        setError('Access denied. SuperAdmin account not found.');
        return;
      }
      navigate('/superadmin/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid SuperAdmin credentials. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #E64C31',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: 'Arial, sans-serif',
    padding: '20px'
  };

  const cardStyle = {
    width: '6in',
    maxWidth: '90%',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    margin: '0 auto'
  };

  const buttonStyle = {
    backgroundColor: loading ? '#cccccc' : '#E64C31',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '6px',
    width: '100%',
    fontSize: '16px',
    cursor: loading ? 'not-allowed' : 'pointer'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    opacity: loading ? 0.6 : 1
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={{
          textAlign: 'center',
          color: '#E64C31',
          marginBottom: '10px',
          fontFamily: "'Fraunces', serif",
          fontSize: '30px',
          fontWeight: 'bold'
        }}>
          BespokeBridge
        </h1>
        <h2 style={{ textAlign: 'center', marginBottom: '5px' }}>
          SuperAdmin Login
        </h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
          Developer Access Portal
        </p>

        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#d32f2f',
            padding: '10px',
            marginBottom: '20px',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="superadmin@bespokebridge.com"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={password}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter your password"
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            style={buttonStyle}
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div style={{
          marginTop: '25px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#666'
        }}>
          <p>This is a restricted developer portal for managing organisation client configurations.</p>
        </div>
      </div>
    </div>
  );
}

export default SuperAdminLogin;
